import { Skeleton } from '@mui/material';
import { type Movie, ScreenType } from '@tmdb-graphql-api/schema/types';
import { useMemo, useState } from 'react';
import { MovieDetailsHeader } from '#components/Details/DetailsHeader/MovieDetailsHeader.tsx';
import { PlayerSelectorTabs } from '#components/PlayerSelectorTabs/index.tsx';
import { Section, SectionColor } from '#components/Section/index.tsx';
import { type TileItem } from '#components/Tile/types.ts';
import { useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { ImageSize, PosterSize } from '#enums/imageSizes.ts';
import { ScreenFormatType, SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { disable as disableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { get } from '#helpers/otherHelpers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { SectionHeading } from '#styled.ts';
import { type ActiveVideo } from '#types.ts';
import { BackdropsCarousel } from '../../BackdropsCarousel/index.tsx';
import { CastAndCrewCarousel } from '../../CastAndCrew/Carousel/index.tsx';
import { Query as CastAndCrewHighlights } from '../../CastAndCrew/Highlights/index.ts';
import { CollectionCarousel } from '../../CollectionCarousel/index.tsx';
import { PlayerSelectorModal } from '../../Modal/PlayerSelectorModal.tsx';
import { Overview } from '../../Overview/index.tsx';
import { PosterImage } from '../../PosterImage/index.tsx';
import { RecommendationsCarousel } from '../../RecommendationsCarousel/index.tsx';
import { SimilarCarousel } from '../../SimilarCarousel/index.tsx';
import { JustWatchLogo } from '../../WatchProviders/JustWatchLogo.tsx';
import { WatchProvidersQuery } from '../../WatchProviders/index.ts';
import {
  CastAndCrewHighlightsContainer,
  DetailsBody,
  PosterImageContainer,
  PosterImageOverviewContainer,
  PoweredBy,
  WatchProvidersHeadingContainer,
} from '../styled.ts';

export type MovieDetailsProps = {
  data: Movie;
  setActivePreview?: (item: Pick<TileItem, 'id' | 'type'>) => void;
};

export const MovieDetails = ({ data, setActivePreview }: MovieDetailsProps) => {
  const { id, overview, posterPath, status, title } = data;
  const { config, uaParser } = useToolbelt();
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | undefined>();
  const isAncestorSlideoutDrawer = useIsAncestor(Ancestor.ListingPreview);

  const memoMovieDetails = useMemo(
    () => (
      <>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <MovieDetailsHeader
            data={data}
            linkTitle={isAncestorSlideoutDrawer}
            setActivePreview={setActivePreview}
            showExpandIcon={isAncestorSlideoutDrawer}
          />
        </Section>
        <Section color={SectionColor.Black}>
          <PlayerSelectorTabs id={id} type={ScreenFormatType.Movie} />
        </Section>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <DetailsBody>
            <PosterImageContainer>
              <PosterImage
                id={id}
                src={buildImagePath(posterPath, ImageSize.Poster, PosterSize.MD, config.images)}
                status={status}
                title={title}
                type={SectionType.Movie}
              />
            </PosterImageContainer>
            <PosterImageOverviewContainer>
              {overview ? <Overview content={overview} id={id} name={title} /> : undefined}
            </PosterImageOverviewContainer>
            <CastAndCrewHighlightsContainer hasOverview={!!overview}>
              <CastAndCrewHighlights id={id} type={ScreenFormatType.Movie} />
            </CastAndCrewHighlightsContainer>
          </DetailsBody>
        </Section>
        {!isAncestorSlideoutDrawer && (
          <>
            <WatchProvidersQuery id={id} type={ScreenType.Movie}>
              {watchProviders =>
                watchProviders ? (
                  <Section
                    color={SectionColor.Grey}
                    threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}
                    title={
                      <WatchProvidersHeadingContainer>
                        {get<number>(watchProviders, 'props.providers.length') > 0 ? (
                          <>
                            <SectionHeading variant="h6">Where to watch</SectionHeading>
                            <div>
                              <PoweredBy>Powered by</PoweredBy>
                              <JustWatchLogo />
                            </div>
                          </>
                        ) : (
                          <Skeleton sx={{ fontSize: '1.25rem' }} variant="text" width={120} />
                        )}
                      </WatchProvidersHeadingContainer>
                    }
                  >
                    {watchProviders}
                  </Section>
                ) : undefined
              }
            </WatchProvidersQuery>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="cast"
              type={ScreenFormatType.Movie}
            >
              {castCarousel => (castCarousel ? <Section title="Cast">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <CastAndCrewCarousel
              id={id}
              setActivePreview={setActivePreview}
              subType="crew"
              type={ScreenFormatType.Movie}
            >
              {castCarousel => (castCarousel ? <Section title="Crew">{castCarousel}</Section> : undefined)}
            </CastAndCrewCarousel>
            <CollectionCarousel id={id} setActivePreview={setActivePreview}>
              {collectionCarousel =>
                collectionCarousel ? <Section title="Collection">{collectionCarousel}</Section> : undefined
              }
            </CollectionCarousel>
            <RecommendationsCarousel id={id} setActivePreview={setActivePreview} type={ScreenType.Movie}>
              {recommendationsCarousel =>
                recommendationsCarousel ? <Section title="Recommended">{recommendationsCarousel}</Section> : undefined
              }
            </RecommendationsCarousel>
            <BackdropsCarousel id={id} title={title} type={ScreenType.Movie}>
              {backdropsCarousel => <Section title="Photos">{backdropsCarousel}</Section>}
            </BackdropsCarousel>
            <SimilarCarousel id={id} setActivePreview={setActivePreview} type={ScreenType.Movie}>
              {similarCarousel => (similarCarousel ? <Section title="Similar">{similarCarousel}</Section> : undefined)}
            </SimilarCarousel>
          </>
        )}
      </>
    ),
    [id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      {activeVideo?.src && activeVideo.src.length > 0 ? (
        <PlayerSelectorModal
          activeVideo={activeVideo}
          onClose={() => {
            setActiveVideo(undefined);
            void disableFullscreenLock(uaParser);
          }}
          type={ScreenType.Movie}
        />
      ) : undefined}
      {memoMovieDetails}
    </div>
  );
};
